import { type FormEvent, useId, useState } from 'react';

import {
  NOTE_CARD_PICKER_CANCEL,
  NOTE_CARD_PICKER_LEGEND,
  NOTE_CARD_PICKER_SUBMIT,
  NOTE_CARD_PICKER_UNAVAILABLE,
} from '../brief-copy.ts';

/** A card a note can go under: its research question's id and words. */
export interface NoteCard {
  rqId: string;
  text: string;
}

export interface NoteCardPickerProps {
  /** The Brief's cards, or `null` when they could not be read. */
  cards: readonly NoteCard[] | null;
  busy: boolean;
  onPick: (rqId: string) => void;
  onCancel: () => void;
}

/**
 * Which card a note goes under, asked only when neither the question nor its answer names one: one
 * choice per card, then Add the note.
 */
export const NoteCardPicker = ({ cards, busy, onPick, onCancel }: NoteCardPickerProps) => {
  const [picked, setPicked] = useState<string | null>(null);
  const name = useId();

  if (cards === null) {
    return (
      <div className="follow-up-note-picker" data-test-id="follow-up-note-picker">
        <p role="alert">{NOTE_CARD_PICKER_UNAVAILABLE}</p>
        <button type="button" onClick={onCancel}>
          {NOTE_CARD_PICKER_CANCEL}
        </button>
      </div>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (picked !== null) {
      onPick(picked);
    }
  };

  return (
    <form className="follow-up-note-picker" data-test-id="follow-up-note-picker" onSubmit={submit}>
      <fieldset>
        <legend>{NOTE_CARD_PICKER_LEGEND}</legend>
        {cards.map((card) => (
          <label key={card.rqId} className="follow-up-note-card">
            <input
              type="radio"
              name={name}
              value={card.rqId}
              data-test-id="follow-up-note-card"
              checked={picked === card.rqId}
              onChange={() => setPicked(card.rqId)}
            />
            <span>{card.text}</span>
          </label>
        ))}
      </fieldset>
      <div className="follow-up-choice-buttons">
        <button
          type="submit"
          data-test-id="follow-up-note-submit"
          disabled={busy || picked === null}
        >
          {NOTE_CARD_PICKER_SUBMIT}
        </button>
        <button type="button" data-test-id="follow-up-note-cancel" onClick={onCancel}>
          {NOTE_CARD_PICKER_CANCEL}
        </button>
      </div>
    </form>
  );
};
